import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import TicketEditModal from "./TicketEditModal";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

export default function TodaysSessions() {
  const { instance, accounts } = useMsal();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);

  async function acquireAccessToken() {
    if (accounts.length === 0) throw new Error("No logged in user.");
    const account = accounts[0];
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn("Session expired, redirecting to login...");
        await instance.loginRedirect({
          scopes: apiScopes,
          account,
        });
        return null;
      }
      throw error;
    }
  }

  async function loadTickets() {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      const res = await fetch("/api/me/time/tickets", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (err) {
      console.error("Error loading tickets:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTickets();
  }, [accounts, instance]);

  // Reload on time-updated event
  useEffect(() => {
    function handleTimeUpdated() {
      loadTickets();
    }
    window.addEventListener("time-updated", handleTimeUpdated);
    return () => window.removeEventListener("time-updated", handleTimeUpdated);
  }, [accounts, instance]);

  // Filter to today's completed tickets only
  const todaysTickets = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return tickets.filter((t) => {
      if (t.state !== "completed") return false;
      const ticketDate = t.startTime ? new Date(t.startTime) : null;
      if (!ticketDate) return false;
      return ticketDate >= startOfToday;
    });
  }, [tickets]);

  // Get sync status
  function getSyncStatus(t) {
    if (t.syncedToJobBoss === true || t.syncedToJobBoss === 1)
      return { icon: "check", color: "text-emerald-600", bg: "bg-emerald-100" };
    if (t.syncError)
      return { icon: "error", color: "text-red-600", bg: "bg-red-100" };
    return { icon: "pending", color: "text-yellow-600", bg: "bg-yellow-100" };
  }

  // Format duration
  function formatDuration(seconds) {
    if (seconds == null || seconds === 0) return "0m";
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
  }

  // Format time
  function formatTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }

  // Calculate total time today
  const totalMinutes = useMemo(() => {
    return todaysTickets.reduce((acc, t) => {
      return acc + Math.round((t.durationSeconds || 0) / 60);
    }, 0);
  }, [todaysTickets]);

  // Save edited ticket
  async function saveTicketEdit(ticketId, updates) {
    const accessToken = await acquireAccessToken();

    const res = await fetch(`/api/me/time/tickets/${ticketId}`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || "Failed to update ticket");
    }

    // Reload tickets to show updated data
    await loadTickets();
  }

  if (loading && !tickets.length) {
    return null; // Don't show loading state, just hide until ready
  }

  if (todaysTickets.length === 0) {
    return null; // Don't show section if no sessions today
  }

  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg hover:shadow-purple-500/10 transition-shadow">
      {/* Header - Clickable to collapse */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-gray-500 transition-transform ${collapsed ? "" : "rotate-90"}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h3 className="text-sm font-semibold text-purple-600">Today's Sessions</h3>
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
            {todaysTickets.length} completed
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Total:</span>
          <span className="font-semibold text-emerald-600">
            {totalMinutes < 60
              ? `${totalMinutes}m`
              : `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`}
          </span>
        </div>
      </button>

      {/* Content */}
      {!collapsed && (
        <div className="border-t border-gray-200">
          <div className="max-h-[200px] overflow-y-auto">
            {todaysTickets.map((t) => {
              const syncStatus = getSyncStatus(t);
              const jobNum = t.jobNumber || t.jobId;
              const jobName = t.jobName || t.projectName || "";

              return (
                <div
                  key={t.ticketId || t.id}
                  className="px-4 py-2 border-b border-gray-100 last:border-b-0 flex items-center gap-3 text-sm hover:bg-slate-200"
                >
                  {/* Sync Status */}
                  <div className={`w-6 h-6 rounded-full ${syncStatus.bg} flex items-center justify-center`}>
                    {syncStatus.icon === "check" && (
                      <svg className={`w-4 h-4 ${syncStatus.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {syncStatus.icon === "error" && (
                      <svg className={`w-4 h-4 ${syncStatus.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {syncStatus.icon === "pending" && (
                      <svg className={`w-4 h-4 ${syncStatus.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>

                  {/* Time */}
                  <div className="w-16 text-gray-500 text-xs">
                    {formatTime(t.startTime)}
                  </div>

                  {/* Job Number */}
                  <div className="w-20 font-mono text-gray-900">
                    {jobNum}
                  </div>

                  {/* Operation */}
                  <div className="w-28 text-purple-600 text-xs font-medium">
                    {t.operationCode || "—"}
                  </div>

                  {/* Job Name */}
                  <div className="flex-1 truncate text-gray-700" title={jobName}>
                    {jobName || <span className="text-gray-500">—</span>}
                  </div>

                  {/* Qty */}
                  <div className="w-12 text-right text-amber-600 font-medium">
                    {t.qty ?? t.piecesFinished ?? "—"}
                  </div>

                  {/* Duration */}
                  <div className="w-14 text-right font-mono text-gray-600">
                    {formatDuration(t.durationSeconds)}
                  </div>

                  {/* Edit Button */}
                  <div className="w-12 text-right">
                    <button
                      onClick={() => setEditingTicket(t)}
                      className="px-2 py-1 text-[10px] bg-transparent text-purple-600 hover:text-purple-700 border border-purple-300 hover:border-purple-400 rounded transition-colors"
                      title="Edit ticket"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Edit Ticket Modal */}
      {editingTicket && (
        <TicketEditModal
          ticket={editingTicket}
          onSave={saveTicketEdit}
          onClose={() => setEditingTicket(null)}
        />
      )}
    </section>
  );
}
